import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sprout, Flame, CalendarDays, ArrowRight, Check, Sparkles, HeartHandshake, X } from "lucide-react";
import { usePortalAuth } from "@/context/PortalAuth";
import { portalApi } from "@/lib/portalApi";

const ease = [0.22, 1, 0.36, 1];
const greet = () => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening"; };

const STEP_LABEL = {
  invitation: "Receive today's invitation",
  choose_challenge: "Choose today's challenge",
  challenge: "Continue your challenge",
  reflection: "Complete your reflection",
};

const daysUntil = (iso) => Math.max(0, Math.ceil((new Date(iso) - new Date()) / 86400000));

export default function Dashboard() {
  const { user } = usePortalAuth();
  const navigate = useNavigate();
  const [day, setDay] = useState(null);
  const [progress, setProgress] = useState(null);
  const [calls, setCalls] = useState([]);
  const [moment, setMoment] = useState(null);
  const [witness, setWitness] = useState(null);

  useEffect(() => {
    portalApi.get("/portal/day").then((r) => setDay(r.data)).catch(() => {});
    portalApi.get("/portal/progress").then((r) => setProgress(r.data)).catch(() => {});
    portalApi.get("/portal/calls").then((r) => setCalls(r.data)).catch(() => {});
    portalApi.get("/portal/moments").then((r) => setMoment(r.data.moment)).catch(() => {});
    portalApi.get("/portal/witness/status").then((r) => setWitness(r.data)).catch(() => {});
  }, []);

  const dismissMoment = () => { if (moment) portalApi.post("/portal/moments/seen", { id: moment.id }).catch(() => {}); setMoment(null); };

  const firstName = (user?.name || "friend").split(" ")[0];
  const week = day?.week ?? progress?.current_week;
  const theme = day?.theme || day?.movement?.name;
  const pct = week ? Math.round((week / 24) * 100) : 0;
  const nextCall = calls.filter((c) => { const d = daysUntil(c.starts_at); return new Date(c.starts_at) >= new Date() && d <= 60; })[0];
  const complete = day?.completed;
  const started = !!day?.week;
  const label = STEP_LABEL[day?.step] || "Continue today's journey";

  const item = { initial: { opacity: 0, y: 18 }, animate: (i) => ({ opacity: 1, y: 0, transition: { delay: 0.08 * i, duration: 0.7, ease } }) };

  return (
    <div data-testid="portal-dashboard" className="max-w-2xl mx-auto text-center pt-6 md:pt-12">
      <motion.p custom={0} variants={item} initial="initial" animate="animate" className="tracking-[0.25em] text-[11px] uppercase text-[#A9724E] mb-5">
        {started ? <>Week {week}{theme ? ` · ${theme}` : ""}</> : "Your journey"}
      </motion.p>
      <motion.h1 custom={1} variants={item} initial="initial" animate="animate" className="font-serif-display font-light text-4xl md:text-6xl mb-6">
        {greet()}, {firstName}.
      </motion.h1>
      <motion.p custom={2} variants={item} initial="initial" animate="animate" className="font-serif-display text-xl md:text-2xl italic text-[#5E4B3C] leading-relaxed max-w-lg mx-auto mb-12">
        {!started ? "Rest here a moment. Your first invitation arrives when your cohort begins."
          : complete ? "Today's journey is complete. Nothing more to do — only to rest in what you received."
          : "There is nothing to finish today. Only an invitation to receive."}
      </motion.p>

      {started && (
        <motion.div custom={3} variants={item} initial="initial" animate="animate">
          {complete ? (
            <div data-testid="journey-complete-card" className="inline-flex items-center gap-3 rounded-2xl bg-[#EAE3D9] border border-[#A9724E]/30 px-8 py-6">
              <span className="w-11 h-11 rounded-full bg-[#A9724E] text-[#F4EFE6] flex items-center justify-center"><Check className="w-5 h-5" strokeWidth={2} /></span>
              <div className="text-left"><p className="font-serif-display text-xl">Today's journey complete</p><p className="text-sm text-[#5E4B3C] font-body">Come back tomorrow.</p></div>
            </div>
          ) : (
            <button onClick={() => navigate("/portal/flow")} data-testid="continue-journey"
              className="group inline-flex items-center gap-3 rounded-full bg-[#2A2825] text-[#F4EFE6] px-9 py-4 text-base tracking-wide hover:bg-[#413d38] transition-colors">
              <Sprout className="w-5 h-5 text-[#E8B486]" strokeWidth={1.5} />{label}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
            </button>
          )}
        </motion.div>
      )}

      <motion.div custom={4} variants={item} initial="initial" animate="animate" className="mt-16 grid grid-cols-3 gap-4 md:gap-8 border-t border-[#E0D8C9] pt-10">
        <Stat testid="stat-progress" icon={Sprout} value={`${pct}%`} label="Garden" />
        <Stat testid="stat-streak" icon={Flame} value={progress?.streak ?? 0} label={`day${(progress?.streak ?? 0) === 1 ? "" : "s"} streak`} />
        <Stat testid="stat-call" icon={CalendarDays} value={nextCall ? daysUntil(nextCall.starts_at) : "—"} label={nextCall ? (daysUntil(nextCall.starts_at) === 1 ? "day to gather" : "days to gather") : "no gathering"} />
      </motion.div>

      {moment && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0, transition: { delay: 0.5, duration: 0.7, ease } }}
          data-testid="moment-card" className="mt-12 relative bg-[#EAE3D9] border border-[#A9724E]/30 rounded-2xl p-7 text-left">
          <button onClick={dismissMoment} data-testid="moment-dismiss" className="absolute top-4 right-4 text-[#8a7a6a] hover:text-[#A9724E]"><X className="w-4 h-4" strokeWidth={1.5} /></button>
          <p className="tracking-[0.2em] text-[10px] uppercase text-[#A9724E] mb-3 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />Remember this?</p>
          <p className="font-serif-display text-xl md:text-2xl italic text-[#2A2825] leading-relaxed">“{moment.text}”</p>
          <p className="text-sm text-[#5E4B3C] font-body mt-4">You wrote this{moment.week ? ` back in Week ${moment.week}` : ""}. Look how far you've come.</p>
        </motion.div>
      )}

      {witness?.needs_love && (
        <motion.button onClick={() => navigate("/portal/duo")} initial={{ opacity: 0 }} animate={{ opacity: 1, transition: { delay: 0.6 } }}
          data-testid="witness-nudge" className="mt-6 w-full bg-white/60 border border-[#E0D8C9] rounded-2xl p-5 flex items-center gap-3 text-left hover:border-[#A9724E] transition-colors">
          <span className="w-10 h-10 rounded-full bg-[#EAE3D9] text-[#A9724E] flex items-center justify-center shrink-0"><HeartHandshake className="w-5 h-5" strokeWidth={1.5} /></span>
          <span className="flex-1"><span className="block font-serif-display text-[#2A2825]">{witness.partner.name.split(" ")[0]} may need some prayer today.</span><span className="text-sm text-[#5E4B3C] font-body">Send a word of love in your Witness Room →</span></span>
        </motion.button>
      )}
    </div>
  );
}

const Stat = ({ icon: Icon, value, label, testid }) => (
  <div data-testid={testid} className="flex flex-col items-center">
    <Icon className="w-5 h-5 text-[#A9724E] mb-3" strokeWidth={1.5} />
    <p className="font-serif-display text-3xl md:text-4xl leading-none">{value}</p>
    <p className="text-xs text-[#8a7a6a] font-body mt-1.5">{label}</p>
  </div>
);
